import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowPathIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    PencilIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { useToast } from '@/hooks/useToast';
import { manageEvaluationInvite, resendCampaignEmails } from '../services/evaluationsService';
import {
    EvaluationCampaignOverview,
    EvaluationEvaluatorCard,
    EvaluationGroupColor,
    getEvaluationCampaignOverview,
} from '../services/evaluationOverview';

type GroupFilter = 'all' | 'pending' | EvaluationGroupColor;
type ActionMode = 'copy_link' | 'resend_email';

const groupColorClasses: Record<EvaluationGroupColor, string> = {
    green: 'border-green-200 bg-green-50/50',
    yellow: 'border-amber-200 bg-amber-50/60',
    red: 'border-red-200 bg-red-50/60',
    gray: 'border-gray-200 bg-gray-50/80',
};

const statusBadgeClasses: Record<EvaluationEvaluatorCard['status'], string> = {
    completed: 'bg-green-100 text-green-800',
    opened: 'bg-amber-100 text-amber-800',
    sent: 'bg-sky-100 text-sky-800',
    not_sent: 'bg-gray-100 text-gray-700',
    unavailable: 'bg-slate-100 text-slate-700',
    missing: 'bg-slate-100 text-slate-500',
};

const roleBadgeClasses: Record<EvaluationEvaluatorCard['type'], string> = {
    SELF: 'bg-indigo-50 text-indigo-700',
    LEADER: 'bg-amber-50 text-amber-700',
    CLIENT: 'bg-emerald-50 text-emerald-700',
};

const filterOptionClasses = (active: boolean) => active
    ? 'border-primary-200 bg-primary-50 text-primary-700'
    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900';

const SummaryCard = ({
    label,
    total,
    byType,
    accent,
    helperText,
}: {
    label: string;
    total: number;
    byType: Record<'SELF' | 'LEADER' | 'CLIENT', number>;
    accent: string;
    helperText: string;
}) => (
    <div className={`rounded-2xl border p-5 shadow-sm ${accent}`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-2 text-3xl font-semibold text-gray-900">{total}</p>
        <p className="mt-2 text-xs text-gray-600">{helperText}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-600">
            <span className="rounded-full bg-white/80 px-2.5 py-1">Colaborador {byType.SELF}</span>
            <span className="rounded-full bg-white/80 px-2.5 py-1">Responsável {byType.LEADER}</span>
            <span className="rounded-full bg-white/80 px-2.5 py-1">Cliente {byType.CLIENT}</span>
        </div>
    </div>
);

function EvaluationCard({
    collaboratorId,
    card,
    loadingAction,
    onCopyLink,
    onResendInvite,
}: {
    collaboratorId: string;
    card: EvaluationEvaluatorCard;
    loadingAction: { key: string; mode: ActionMode } | null;
    onCopyLink: (collaboratorId: string, card: EvaluationEvaluatorCard) => Promise<void>;
    onResendInvite: (collaboratorId: string, card: EvaluationEvaluatorCard) => Promise<void>;
}) {
    const actionKey = `${collaboratorId}:${card.type}`;
    const isCopyLoading = loadingAction?.key === actionKey && loadingAction.mode === 'copy_link';
    const isResendLoading = loadingAction?.key === actionKey && loadingAction.mode === 'resend_email';
    const displayName = card.helperText && card.status === 'missing'
        ? card.helperText
        : (card.recipientName || 'Sem destinatário');
    const showAvatar = (card.type === 'SELF' || card.type === 'LEADER') && card.status !== 'missing';

    return (
        <div className="flex h-full flex-col rounded-2xl border border-white/80 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${roleBadgeClasses[card.type]}`}>
                        {card.title}
                    </span>
                    <div className="mt-2 flex min-w-0 items-center gap-2">
                        {showAvatar ? (
                            <CollaboratorAvatar
                                photoUrl={card.photoUrl}
                                name={displayName}
                                size="small"
                            />
                        ) : null}
                        <p className="truncate text-sm font-semibold text-gray-900" title={displayName}>
                            {displayName}
                        </p>
                    </div>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[card.status]}`}>
                    {card.statusLabel}
                </span>
            </div>

            <div className="mt-4 pt-1">
                <div className="flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={!card.canCopyLink || isResendLoading}
                        loading={isCopyLoading}
                        className="rounded-lg px-2.5 py-1 text-xs"
                        onClick={() => void onCopyLink(collaboratorId, card)}
                    >
                        Copiar link
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={!card.canResend || isCopyLoading}
                        loading={isResendLoading}
                        className="rounded-lg px-2.5 py-1 text-xs"
                        onClick={() => void onResendInvite(collaboratorId, card)}
                    >
                        Reenviar e-mail
                    </Button>
                </div>
            </div>
        </div>
    );
}

function groupHasPendingWork(cards: EvaluationEvaluatorCard[]) {
    return cards.some((card) => ['not_sent', 'sent', 'opened'].includes(card.status));
}

export const EvaluationDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [overview, setOverview] = useState<EvaluationCampaignOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [resendingAll, setResendingAll] = useState(false);
    const [actionLoading, setActionLoading] = useState<{ key: string; mode: ActionMode } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');

    const loadOverview = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
        if (!id) return;

        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const data = await getEvaluationCampaignOverview(id);
            setOverview(data);
            setLoadError(null);
        } catch (error) {
            console.error(error);
            const message = silent
                ? 'Não foi possível atualizar o acompanhamento. Você está vendo os dados anteriores.'
                : 'Não foi possível carregar o acompanhamento da campanha.';
            setLoadError(message);
            if (!silent) {
                setOverview(null);
            }
            toast.error('Erro', message);
        } finally {
            if (silent) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    }, [id, toast]);

    useEffect(() => {
        void loadOverview();
    }, [loadOverview]);

    const handleResendAll = async () => {
        if (!id || !overview || overview.campaign.status !== 'active') return;
        if (!confirm('Deseja reenviar os convites ainda não concluídos desta campanha?')) return;

        setResendingAll(true);
        try {
            const result = await resendCampaignEmails(id);
            toast.success('Sucesso', `${result.count} convite(s) reenviado(s).`);
            await loadOverview({ silent: true });
        } catch (error) {
            console.error(error);
            toast.error('Erro', 'Não foi possível reenviar os convites.');
        } finally {
            setResendingAll(false);
        }
    };

    const handleCopyLink = async (collaboratorId: string, card: EvaluationEvaluatorCard) => {
        if (!id || !navigator.clipboard) {
            toast.error('Erro', 'A cópia de link não está disponível neste navegador.');
            return;
        }

        const actionKey = `${collaboratorId}:${card.type}`;
        setActionLoading({ key: actionKey, mode: 'copy_link' });
        try {
            const result = await manageEvaluationInvite({
                campaignId: id,
                collaboratorId,
                type: card.type,
                mode: 'copy_link',
                origin: window.location.origin,
            });
            await navigator.clipboard.writeText(result.link);
            toast.success('Sucesso', 'Link copiado para a área de transferência.');
            await loadOverview({ silent: true });
        } catch (error) {
            console.error(error);
            toast.error('Erro', 'Não foi possível copiar o link.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleResendInvite = async (collaboratorId: string, card: EvaluationEvaluatorCard) => {
        if (!id || !overview) return;

        const actionKey = `${collaboratorId}:${card.type}`;
        setActionLoading({ key: actionKey, mode: 'resend_email' });
        try {
            await manageEvaluationInvite({
                campaignId: id,
                collaboratorId,
                type: card.type,
                mode: 'resend_email',
                origin: window.location.origin,
            });
            toast.success('Sucesso', 'Convite reenviado com sucesso.');
            await loadOverview({ silent: true });
        } catch (error) {
            console.error(error);
            toast.error('Erro', 'Não foi possível reenviar o convite.');
        } finally {
            setActionLoading(null);
        }
    };

    const filterTotals = useMemo(() => {
        const totals: Record<GroupFilter, number> = {
            all: overview?.collaboratorGroups.length || 0,
            pending: 0,
            green: 0,
            yellow: 0,
            red: 0,
            gray: 0,
        };

        if (!overview) {
            return totals;
        }

        overview.collaboratorGroups.forEach((group) => {
            totals[group.color] += 1;
            if (groupHasPendingWork(group.cards)) {
                totals.pending += 1;
            }
        });

        return totals;
    }, [overview]);

    const visibleGroups = useMemo(() => {
        if (!overview) {
            return [];
        }

        const normalizedSearch = searchTerm.trim().toLowerCase();

        return overview.collaboratorGroups.filter((group) => {
            const matchesSearch = !normalizedSearch || [
                group.collaboratorName,
                group.collaboratorEmail,
                ...group.cards.flatMap((card) => [card.recipientName, card.recipientEmail]),
            ].filter(Boolean).some((value) => value!.toLowerCase().includes(normalizedSearch));

            if (!matchesSearch) {
                return false;
            }

            if (groupFilter === 'all') {
                return true;
            }

            if (groupFilter === 'pending') {
                return groupHasPendingWork(group.cards);
            }

            return group.color === groupFilter;
        });
    }, [groupFilter, overview, searchTerm]);

    if (loading && !overview) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <p className="text-sm font-medium text-gray-900">Carregando acompanhamento da campanha...</p>
                <p className="mt-2 text-sm text-gray-500">Preparando progresso, status e ações por avaliador.</p>
            </div>
        );
    }

    if (loadError && !overview) {
        return (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-8 shadow-sm">
                <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-red-600" aria-hidden />
                    <div className="space-y-4">
                        <div>
                            <h1 className="text-lg font-semibold text-red-900">Não foi possível carregar a campanha</h1>
                            <p className="mt-1 text-sm text-red-800">{loadError}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button variant="primary" onClick={() => void loadOverview()}>
                                Tentar novamente
                            </Button>
                            <Button variant="outline" onClick={() => navigate('/people/evaluations')}>
                                Voltar para campanhas
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!overview) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <h1 className="text-lg font-semibold text-gray-900">Campanha não encontrada</h1>
                <p className="mt-2 text-sm text-gray-500">Verifique se a campanha ainda existe ou se o link está correto.</p>
                <div className="mt-4">
                    <Button variant="outline" onClick={() => navigate('/people/evaluations')}>
                        Voltar para campanhas
                    </Button>
                </div>
            </div>
        );
    }

    const campaignStatusLabel = overview.campaign.status === 'active'
        ? 'Ativa'
        : overview.campaign.status === 'closed'
            ? 'Encerrada'
            : 'Rascunho';

    const campaignStatusClass = overview.campaign.status === 'active'
        ? 'bg-green-100 text-green-800'
        : overview.campaign.status === 'closed'
            ? 'bg-gray-100 text-gray-700'
            : 'bg-amber-100 text-amber-800';

    const filterOptions: Array<{ value: GroupFilter; label: string }> = [
        { value: 'all', label: 'Todos' },
        { value: 'pending', label: 'Com pendência' },
        { value: 'red', label: 'Atenção' },
        { value: 'yellow', label: 'Em andamento' },
        { value: 'green', label: 'Concluídos' },
        { value: 'gray', label: 'Sem tracking' },
    ];

    const pageActions = (
        <>
            {overview.campaign.status === 'active' ? (
                <Button
                    variant="outline"
                    loading={resendingAll}
                    onClick={handleResendAll}
                    leftIcon={<EnvelopeIcon className="h-4 w-4" aria-hidden />}
                >
                    Reenviar todos
                </Button>
            ) : null}
            <Button
                variant="primary"
                onClick={() => navigate(`/people/evaluations/${id}/edit`)}
                leftIcon={<PencilIcon className="h-4 w-4" aria-hidden />}
            >
                Editar campanha
            </Button>
        </>
    );

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo="/people/evaluations"
                icon={<ClipboardDocumentListIcon className="h-6 w-6" aria-hidden />}
                badge="Avaliações"
                title={overview.campaign.questionnaireTitle || 'Campanha de avaliação'}
                description={`Período ${overview.campaign.startDate.toLocaleDateString('pt-BR')} até ${overview.campaign.endDate.toLocaleDateString('pt-BR')}`}
                actions={pageActions}
            />

            {loadError ? (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-700" aria-hidden />
                    <span>{loadError}</span>
                    <Button size="sm" variant="ghost" onClick={() => void loadOverview({ silent: true })}>
                        Tentar atualizar
                    </Button>
                </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${campaignStatusClass}`}>
                    {campaignStatusLabel}
                </span>
                <span className="text-sm text-gray-600">
                    {overview.collaboratorGroups.length} colaborador(es) avaliados
                </span>
                <span className="text-sm text-gray-600">
                    {overview.summary.totalExpected} convite(s) esperados
                </span>
                {refreshing ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">
                        <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                        Atualizando dados
                    </span>
                ) : null}
            </div>

            {overview.summary.unavailable.total > 0 ? (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-700 shadow-sm">
                    <InformationCircleIcon className="h-5 w-5 text-slate-500" aria-hidden />
                    <span>
                        {overview.summary.unavailable.total} convite(s) estão sem histórico confiável. Eles aparecem nos cards, mas não entram em Pendentes.
                    </span>
                </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                <SummaryCard
                    label="Enviados"
                    total={overview.summary.sent.total}
                    byType={overview.summary.sent.byType}
                    accent="border-sky-200 bg-sky-50/70"
                    helperText="Convites enviados e ainda não acessados."
                />
                <SummaryCard
                    label="Abertos"
                    total={overview.summary.opened.total}
                    byType={overview.summary.opened.byType}
                    accent="border-amber-200 bg-amber-50/70"
                    helperText="Formulários acessados, mas ainda não concluídos."
                />
                <SummaryCard
                    label="Concluídos"
                    total={overview.summary.completed.total}
                    byType={overview.summary.completed.byType}
                    accent="border-green-200 bg-green-50/70"
                    helperText="Avaliações finalizadas com sucesso."
                />
                <SummaryCard
                    label="Pendentes"
                    total={overview.summary.pending.total}
                    byType={overview.summary.pending.byType}
                    accent="border-gray-200 bg-gray-50/90"
                    helperText="Convites ainda não iniciados."
                />
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden />
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Buscar por colaborador, e-mail ou destinatário..."
                            className="block w-full rounded-xl border-gray-300 pl-10 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {filterOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setGroupFilter(option.value)}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition ${filterOptionClasses(groupFilter === option.value)}`}
                            >
                                {option.label}
                                <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs">
                                    {filterTotals[option.value]}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-4 text-sm text-gray-500">
                    Exibindo {visibleGroups.length} de {overview.collaboratorGroups.length} grupo(s).
                </div>
            </div>

            {visibleGroups.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center shadow-sm">
                    <p className="text-base font-semibold text-gray-900">Nenhum grupo encontrado</p>
                    <p className="mt-2 text-sm text-gray-500">
                        Ajuste a busca ou os filtros para localizar os convites que você quer acompanhar.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {visibleGroups.map((group) => {
                        const selfCard = group.cards.find((card) => card.type === 'SELF');

                        return (
                            <section
                                key={group.collaboratorId}
                                className={`rounded-3xl border p-5 shadow-sm ${groupColorClasses[group.color]}`}
                            >
                                <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <CollaboratorAvatar
                                            photoUrl={selfCard?.photoUrl}
                                            name={group.collaboratorName}
                                            size="medium"
                                        />
                                        <div className="min-w-0">
                                            <h2 className="truncate text-base font-semibold text-gray-900">{group.collaboratorName}</h2>
                                            <p className="truncate text-sm text-gray-500">{group.collaboratorEmail || '-'}</p>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full justify-center rounded-lg sm:w-auto"
                                        leftIcon={<ChartBarIcon className="h-4 w-4" aria-hidden />}
                                        onClick={() => navigate(`/people/evaluations/${id}/results/${group.collaboratorId}`)}
                                    >
                                        Resultado
                                    </Button>
                                </div>
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                                    {group.cards.map((card) => (
                                        <EvaluationCard
                                            key={`${group.collaboratorId}-${card.type}`}
                                            collaboratorId={group.collaboratorId}
                                            card={card}
                                            loadingAction={actionLoading}
                                            onCopyLink={handleCopyLink}
                                            onResendInvite={handleResendInvite}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
